import { ArrowLeft, Coins, RefreshCw, Rocket } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatPanel from '../components/agent/ChatPanel';
import LowTokenModal from '../components/agent/LowTokenModal';
import { Message } from '../components/agent/MessageBubble';
import PreviewPanel from '../components/agent/PreviewPanel';
import PublishModal from '../components/agent/PublishModal';
import useEntity from '../hooks/useEntity';
import useTokenBalance from '../hooks/useTokenBalance';
import { agentService, StreamData } from '../services/agentService';
import useToastStore from '../store/useToastStore';

const INITIAL_MESSAGE: Message = {
    id: 'init',
    type: 'ai',
    text: "Hey! 👋 I'm your AI design assistant. I can help you customize your store's look and feel.\n\nTry asking me to:\n• Change the theme or color scheme\n• Update banner transition effects\n• Make category icons square or round\n• Adjust layouts and spacing\n• Modify fonts and typography\n• And much more!\n\nWhat would you like to customize?",
};

const buildPreviewUrl = (entityId: number): string => {
    if (process.env.NODE_ENV === 'production') {
        return `https://${entityId}-preview.mart-24.com`;
    }
    return `http://${entityId}-preview.localhost:8001`;
};

const formatBalance = (n: number): string => {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace('.0', '') + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(1).replace('.0', '') + 'K';
    return n.toString();
};

export default function AgentWorkspacePage() {
    const navigate = useNavigate();
    const { data: entity, isLoading: entityLoading, error: entityError } = useEntity();
    const { data: tokenData, refetch: refetchTokenBalance } = useTokenBalance();
    const toast = useToastStore();

    // ── Chat state ─────────────────────────────────────────────────────────
    const [messages, setMessages] = useState<Message[]>([INITIAL_MESSAGE]);
    const [inputMessage, setInputMessage] = useState('');
    const [isAgentThinking, setIsAgentThinking] = useState(false);
    const [currentActivity, setCurrentActivity] = useState<string | null>(null);
    const [isFirstTurn, setIsFirstTurn] = useState(true);
    // A conversation IS the agent session (like Claude Code). Follow-ups stay in
    // the same conversation; "New chat" mints a fresh one → fresh agent session.
    const [conversationId, setConversationId] = useState<string>(() => crypto.randomUUID());

    // ── Preview state ──────────────────────────────────────────────────────
    const [livePreviewUrl, setLivePreviewUrl] = useState('about:blank');
    const [isPreviewReady, setIsPreviewReady] = useState(false);
    const [showBuildingAnimation, setShowBuildingAnimation] = useState(false);
    // Trigger object passed to PreviewPanel so it can react to SSE preview_ready
    const [previewRefreshTrigger, setPreviewRefreshTrigger] = useState<{ url?: string } | null>(null);

    // ── UI state ───────────────────────────────────────────────────────────
    const [mobileView, setMobileView] = useState<'chat' | 'preview'>('preview');
    const [showLowTokenModal, setShowLowTokenModal] = useState(false);
    const [isZeroToken, setIsZeroToken] = useState(false);

    // ── Publish state ──────────────────────────────────────────────────────
    const [showPublishConfirm, setShowPublishConfirm] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [publishResult, setPublishResult] = useState<{ success: boolean; error?: string } | null>(null);

    // ── Refs for SSE closure stability ─────────────────────────────────────
    const isAgentThinkingRef = useRef(false);
    const refetchTokenBalanceRef = useRef(refetchTokenBalance);
    const eventSourceRef = useRef<EventSource | null>(null);
    const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
    const lastEntityIdRef = useRef<number | null>(null);
    // SSE reconnect counter — incrementing triggers the SSE effect to re-run
    const [sseKey, setSseKey] = useState(0);

    useEffect(() => { isAgentThinkingRef.current = isAgentThinking; }, [isAgentThinking]);
    useEffect(() => { refetchTokenBalanceRef.current = refetchTokenBalance; }, [refetchTokenBalance]);

    // ── Init preview URL when entityId first becomes available ─────────────
    useEffect(() => {
        if (entity?.entityId === undefined || entity?.entityId === null) return;
        if (lastEntityIdRef.current === entity.entityId) return;
        lastEntityIdRef.current = entity.entityId;
        setLivePreviewUrl(buildPreviewUrl(entity.entityId));
        setIsPreviewReady(true);
    }, [entity?.entityId]);

    // ── SSE setup with auto-reconnect ──────────────────────────────────────
    useEffect(() => {
        if (entity?.entityId === undefined || entity?.entityId === null) return;

        const entityId = entity.entityId;

        const addMessage = (text: string) =>
            setMessages(prev => [...prev, { id: crypto.randomUUID(), type: 'ai', text }]);

        const connect = async () => {
            // Clean up previous connection
            eventSourceRef.current?.close();

            try {
                const es = await agentService.getStatusStream(
                    entityId.toString(),
                    (data: StreamData) => {
                        if (data.type === 'heartbeat') return;

                        if (data.type === 'message') {
                            const text = data.data || data.message;
                            if (text) addMessage(text);
                        }

                        if (data.type === 'tool_activity') {
                            setCurrentActivity(data.message ?? null);
                        }

                        if (data.type === 'preview_building') {
                            setShowBuildingAnimation(true);
                        }

                        if (data.type === 'preview_ready') {
                            const url = data.url || data.data || data.message;
                            setPreviewRefreshTrigger({ url });
                            if (window.innerWidth < 768) setMobileView('preview');
                        }

                        if (data.type === 'preview_error') {
                            console.error('[SSE] preview_error:', data.message);
                        }

                        if (data.type === 'finished') {
                            setIsAgentThinking(false);
                            setCurrentActivity(null);
                            setIsFirstTurn(false);
                            refetchTokenBalanceRef.current();
                            setTimeout(() => setShowBuildingAnimation(false), 500);
                        }

                        if (
                            data.type === 'error' ||
                            data.type === 'api_constraint' ||
                            data.type === 'ambiguous_request' ||
                            data.type === 'clarification_needed'
                        ) {
                            setIsAgentThinking(false);
                            setCurrentActivity(null);
                            setShowBuildingAnimation(false);
                            if (data.message) addMessage(data.message);
                        }
                    },
                    (error) => {
                        console.error('[SSE] connection error:', error);

                        if (es.readyState !== EventSource.CLOSED) return;

                        if (isAgentThinkingRef.current) {
                            // Agent was mid-turn — attempt reconnect after 2 s
                            addMessage('Connection interrupted. Reconnecting…');
                            reconnectTimerRef.current = setTimeout(() => {
                                setSseKey(k => k + 1);
                            }, 2000);
                        }

                        setIsAgentThinking(false);
                        setCurrentActivity(null);
                        setShowBuildingAnimation(false);
                    }
                );

                eventSourceRef.current = es;
            } catch (err) {
                console.error('[SSE] setup failed:', err);
                setIsAgentThinking(false);
            }
        };

        connect();

        return () => {
            eventSourceRef.current?.close();
            eventSourceRef.current = null;
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
        };
    // sseKey is intentional: incrementing it forces a reconnect
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entity?.entityId, sseKey]);

    // ── Message sending ────────────────────────────────────────────────────
    const handleSend = useCallback(async () => {
        const text = inputMessage.trim();
        if (!text) return;

        const balance = tokenData?.balance ?? 0;
        if (balance < 1000) {
            setIsZeroToken(balance === 0);
            setShowLowTokenModal(true);
            return;
        }

        setMessages(prev => [...prev, { id: crypto.randomUUID(), type: 'user', text }]);
        setInputMessage('');
        setIsAgentThinking(true);
        setShowBuildingAnimation(true);

        try {
            await agentService.sendMessage(text, conversationId);
        } catch (err) {
            console.error('[AgentWorkspace] sendMessage failed:', err);
            setMessages(prev => [...prev, { id: crypto.randomUUID(), type: 'ai', text: "Sorry, I couldn't process that request." }]);
            setIsAgentThinking(false);
            setShowBuildingAnimation(false);
        }
    }, [inputMessage, tokenData?.balance, conversationId]);

    // ── New chat ───────────────────────────────────────────────────────────
    // Mint a fresh conversation id → the backend starts a fresh agent session,
    // shedding the previous conversation's accumulated context (this is the big
    // cost lever). Follow-ups within a conversation still resume, so continuity
    // is preserved; only an explicit "New chat" resets.
    const handleNewChat = useCallback(() => {
        if (isAgentThinking || isPublishing) return;
        setConversationId(crypto.randomUUID());
        setMessages([INITIAL_MESSAGE]);
        setInputMessage('');
        setIsFirstTurn(true);
    }, [isAgentThinking, isPublishing]);

    // ── Manual preview reload ────────────────────────────────────────────────
    // Reuses the same trigger the SSE preview_ready path uses — a fresh object
    // makes PreviewPanel reload the iframe (cache-busted).
    const handleRefreshPreview = useCallback(() => {
        if (isAgentThinking || isPublishing || !isPreviewReady) return;
        setPreviewRefreshTrigger({ url: livePreviewUrl });
    }, [isAgentThinking, isPublishing, isPreviewReady, livePreviewUrl]);

    // ── Publish ────────────────────────────────────────────────────────────
    const startPublish = useCallback(async () => {
        const balance = tokenData?.balance ?? 0;
        if (balance <= 0) {
            setShowPublishConfirm(false);
            setIsZeroToken(true);
            setShowLowTokenModal(true);
            return;
        }

        setIsPublishing(true);
        setShowPublishConfirm(false);
        setPublishResult(null);
        try {
            const response = await agentService.publish();
            if (response.success) {
                setPublishResult({ success: true });
                toast.showToast('Website published successfully!', 'success');
                refetchTokenBalance();
            } else {
                setPublishResult({ success: false, error: response.error || 'Publishing failed' });
                toast.showToast('Publishing failed. Please try again.', 'error');
            }
        } catch (err: any) {
            setPublishResult({ success: false, error: err?.response?.data?.message || err?.message || 'Failed to publish website. Please try again.' });
            refetchTokenBalance();
            toast.showToast('Failed to publish website', 'error');
        } finally {
            setIsPublishing(false);
        }
    }, [tokenData?.balance, refetchTokenBalance, toast]);

    const hasTokens = (tokenData?.balance ?? 0) > 0;
    const balance = tokenData?.balance;

    return (
        <div className="flex flex-col h-[100dvh] bg-gray-50 font-sans overflow-hidden">
            {/* ── Adaptive top bar (all screen sizes) ─────────────────────── */}
            <header className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm">
                <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-6 h-14">
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="flex items-center justify-center h-10 w-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 group"
                        aria-label="Back to dashboard"
                    >
                        <ArrowLeft className="w-5 h-5 md:group-hover:-translate-x-0.5 transition-transform" />
                    </button>
                    <span className="hidden sm:block text-sm font-semibold text-gray-900 truncate">Store Designer</span>

                    <div className="flex-1" />

                    {/* Reload preview */}
                    <button
                        onClick={handleRefreshPreview}
                        disabled={isAgentThinking || isPublishing || !isPreviewReady}
                        title="Reload preview"
                        aria-label="Reload preview"
                        className="flex items-center justify-center h-10 w-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                    >
                        <RefreshCw className="w-5 h-5" />
                    </button>

                    {/* Balance chip → buy tokens */}
                    <button
                        onClick={() => navigate('/tokens/purchase')}
                        className="flex items-center gap-1.5 h-10 px-2.5 sm:px-3 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors flex-shrink-0"
                        title="Token balance — tap to buy more"
                    >
                        <Coins className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        <span className="text-sm font-bold text-amber-800 tabular-nums">
                            {balance !== undefined ? formatBalance(balance) : '---'}
                        </span>
                        <span className="hidden sm:inline text-xs font-semibold text-amber-700">+ Buy</span>
                    </button>

                    {/* Publish */}
                    <button
                        onClick={() => setShowPublishConfirm(true)}
                        disabled={isAgentThinking || isPublishing || !hasTokens}
                        title={!hasTokens ? 'Purchase tokens to publish' : 'Publish your store live'}
                        className="flex items-center justify-center gap-1.5 h-10 px-3 sm:px-4 bg-success hover:opacity-90 text-white font-semibold rounded-lg shadow-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    >
                        <Rocket className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm">Publish</span>
                    </button>
                </div>

                {/* Segmented Chat / Preview toggle — mobile only */}
                <div className="md:hidden px-2 pb-2">
                    <div className="flex bg-gray-100 rounded-xl p-1">
                        {(['chat', 'preview'] as const).map(tab => (
                            <button
                                key={tab}
                                onClick={() => setMobileView(tab)}
                                className={`flex-1 h-9 rounded-lg text-sm font-semibold capitalize transition-all ${
                                    mobileView === tab
                                        ? 'bg-white text-primary shadow-sm'
                                        : 'text-gray-500'
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            {/* Main layout */}
            <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
                {/* Chat column */}
                <div className={`${mobileView === 'chat' ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-1/2 lg:w-2/5 xl:w-1/3 h-full md:border-r border-gray-200 overflow-hidden`}>
                    <ChatPanel
                        messages={messages}
                        inputMessage={inputMessage}
                        isAgentThinking={isAgentThinking}
                        isPreviewReady={isPreviewReady}
                        isPublishing={isPublishing}
                        currentActivity={currentActivity}
                        tokenBalance={balance}
                        isFirstTurn={isFirstTurn}
                        onInputChange={setInputMessage}
                        onSend={handleSend}
                        onNewChat={handleNewChat}
                    />
                </div>

                {/* Preview column */}
                <div className={`${mobileView === 'preview' ? 'flex' : 'hidden'} md:flex flex-1 h-full md:h-full flex-col bg-gray-50`}>
                    <PreviewPanel
                        entityLoading={entityLoading}
                        entityError={!!entityError}
                        entityId={entity?.entityId ?? null}
                        isPreviewReady={isPreviewReady}
                        livePreviewUrl={livePreviewUrl}
                        isAgentThinking={isAgentThinking}
                        isPublishing={isPublishing}
                        showBuildingAnimation={showBuildingAnimation}
                        onRefreshPreview={() => {}}
                        previewRefreshTrigger={previewRefreshTrigger}
                    />
                </div>
            </div>

            {/* Modals */}
            <PublishModal
                entity={entity}
                showConfirm={showPublishConfirm}
                onConfirmClose={() => setShowPublishConfirm(false)}
                onConfirmPublish={startPublish}
                isPublishing={isPublishing}
                publishResult={publishResult}
                onResultClose={() => setPublishResult(null)}
                onRetry={() => { setPublishResult(null); startPublish(); }}
            />

            {showLowTokenModal && (
                <LowTokenModal
                    isZeroToken={isZeroToken}
                    onClose={() => setShowLowTokenModal(false)}
                />
            )}
        </div>
    );
}
